import { useState } from "react";
import ModalComponent from "../../../../components/modals/ModalFormComponent";
import Label from "../../../../components/form_fields/Label";
import { SubmitHandler, useForm } from "react-hook-form";
import {
	useCreateBenefitGroup,
	useUpdateBenefitGroup,
} from "../../../../services/benefit-group.service";
import {
	FREQUENCY,
	IAddEditTableForm,
	enumToStringConverter,
} from "../../../../helpers/appHelpers";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import FormDescriptionInput from "../../../../components/form_fields/FormDescriptionInput";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import { useManageTableWithSearchParamsData } from "../../../../helpers/useManageTable";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import FormCodeInput from "../../../../components/form_fields/FormCodeInput";
import { BenefitType } from "../../../../models/benefit-type.dto";
import { TaxCodeDto } from "../../../../models/tax-code.dto";

interface IFormInputs {
	code: string;
	name: string;
	description: string;
	benefitTypeInstanceId: number;
	taxCode?: string;
	frequency?: string;
}

type BenefitGroupModalFormProps = IAddEditTableForm & {
	benefitTypes: BenefitType[];
	taxCodes: TaxCodeDto[];
	reloadTableData?: () => void;
};

const BenefitGroupModalForm = ({
	open,
	setOpen,
	title,
	isEditForm,
	tableAPIRef,
	benefitTypes,
	taxCodes,
	reloadTableData,
}: BenefitGroupModalFormProps) => {
	const { currentCompany } = useCompanyStore();
	const { formDataValues } = useEditFormStore();
	const [uploading, setUploading] = useState(false);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: {
			name: isEditForm ? formDataValues?.name : "",
			code: isEditForm ? formDataValues?.code : "",
			description: isEditForm ? formDataValues?.description : "",
			benefitTypeInstanceId: isEditForm
				? formDataValues?.benefitTypeInstanceId
				: undefined,
			taxCode: isEditForm ? formDataValues?.taxCode : "",
			frequency: isEditForm ? formDataValues?.frequency : "",
		},
	});

	const { mutate: createBenefitGroup } = useCreateBenefitGroup();
	const { mutate: updateBenefitGroup } = useUpdateBenefitGroup();
	const { addNewTableData } = useManageTableWithSearchParamsData();

	const onSubmit: SubmitHandler<IFormInputs> = (data: any) => {
		setUploading(true);

		if (isEditForm) {
			updateBenefitGroup(
				{
					payload: data,
					companyId: currentCompany?.id as number,
					id: formDataValues?.id,
				},
				{
					onSuccess: (updatedData) => {
						try {
							// The update API response only returns flat data with benefitTypeInstanceId.
							// Enrich the row with the expanded benefitTypeInstance object so the
							// table cell renderer (which reads `.benefitTypeInstance.name`) stays
							// populated after an in-place row update.
							const benefitTypeInstance =
								benefitTypes.find(
									(bt) => bt.id === updatedData.data.benefitTypeInstanceId,
								) ?? formDataValues?.benefitTypeInstance;

							tableAPIRef?.current.updateRows([
								{
									...updatedData.data,
									benefitTypeInstance,
								},
							]);
						} catch (error) {
							console.error("Error updating table rows:", error);
							// Fall back to a full reload so the relation is fetched from the server.
							reloadTableData?.();
						}
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		} else {
			createBenefitGroup(
				{ companyId: currentCompany?.id as number, payload: data },
				{
					onSuccess: () => {
						// Use reloadTableData (full server re-fetch) instead of addNewTableData
						// to guarantee the new row includes the expanded benefitTypeInstance
						// relation from the list endpoint rather than the flat create response.
						reloadTableData ? reloadTableData() : addNewTableData();
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		}
	};

	const handleClose = () => {
		setOpen(false);
		reset();
	};

	return (
		<ModalComponent open={open} title={title} handleClose={handleClose}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="grid grid-cols-2 gap-x-4 gap-y-6">
					<div className="w-full">
						<Label title="Name" for="name" />
						<input
							autoFocus
							defaultValue={isEditForm ? formDataValues?.name : ""}
							placeholder={"Enter name"}
							type="text"
							{...register("name", {
								required: "Enter name here",
							})}
							id="name"
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.name
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
						/>
						{errors.name && (
							<p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Unique Code" for="code" />
						<FormCodeInput
							register={register}
							attributes={{
								className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									errors.code
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`,
							}}
						/>
						{errors.code && (
							<p className="text-red-500 text-sm mt-1">{errors.code.message}</p>
						)}
					</div>
					<div className="w-full col-span-2">
						<Label title="Description" for="description" />
						<FormDescriptionInput
							register={register}
							attributes={{
								defaultValue: isEditForm ? formDataValues?.description : "",
								className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									errors.description
										? " border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`,
							}}
						/>
						{errors.description && (
							<p className="text-red-500 text-sm mt-1">
								{errors.description.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Benefit Type" for="benefitTypeInstanceId" />
						<select
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.benefitTypeInstanceId
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
							{...register("benefitTypeInstanceId", {
								required: "Select benefit type here",
								valueAsNumber: true,
							})}
						>
							<option value="">Select Type</option>
							{benefitTypes.map((benefitType) => (
								<option key={benefitType.id} value={benefitType.id}>
									{benefitType.name}
								</option>
							))}
						</select>
						{errors.benefitTypeInstanceId && (
							<p className="text-red-500 text-sm mt-1">
								{errors.benefitTypeInstanceId.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Tax Code" for="taxCode" optional />
						<select
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
								errors.taxCode
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
							{...register("taxCode", {
								required: false,
							})}
						>
							<option value="">Select Tax Code</option>
							{taxCodes.map((taxCode) => (
								<option key={taxCode.code} value={taxCode.code}>
									{taxCode.code}
								</option>
							))}
						</select>
						{errors.taxCode && (
							<p className="text-red-500 text-sm mt-1">
								{errors.taxCode.message}
							</p>
						)}
					</div>
					<div className="w-full col-span-2">
						<Label title="Frequency" for="frequency" optional />
						<select
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm mb-2 ${
								errors.frequency
									? " border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
							{...register("frequency")}
						>
							<option value="">Select frequency</option>
							{FREQUENCY.map((item, index) => (
								<option key={index} value={item}>
									{enumToStringConverter(item)}
								</option>
							))}
						</select>
						{errors.frequency && (
							<p className="text-red-500 text-sm mt-1">
								{errors.frequency.message}
							</p>
						)}
					</div>
				</div>

				<div className="w-full flex justify-end mt-8">
					<OrangeButton
						type="submit"
						title={
							uploading
								? "Uploading..."
								: isEditForm
									? "Update Benefit Group"
									: "Add Benefit Group"
						}
						className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
						disabled={uploading}
					/>
				</div>
			</form>
		</ModalComponent>
	);
};

export default BenefitGroupModalForm;
